import { useEffect, useMemo, useState } from 'react';
import { Image3D } from '~/domain/patient/image3d';
import {
    RotatedMip,
    RotatedMipCoverage,
    RotatedMipFrame,
    computeRotatedMip,
    planRotatedMipFrame,
} from '~/domain/services/rotatedMip';
import { percentileWindow } from '~/domain/services/percentileWindow';
import { useImageStreamProgress } from '~/hooks/image/useImageStreamProgress';

/** A display window as [lower, upper], in the volume's stored units. */
export type DisplayWindow = readonly [number, number];

export type RotatedMipResult =
    | {
          state: 'ready';
          mip: RotatedMip;
          /**
           * Taken once from the anterior view and shared by every azimuth: a
           * contrast that changed as the view turns would read as uptake.
           */
          autoWindow: DisplayWindow;
      }
    /** Refused until every slice is in: a gap would read as absent uptake. */
    | { state: 'waitingForSlices'; loaded: number; total: number }
    /** Retrieval ended short: no projection will represent the volume. */
    | { state: 'imageIncomplete'; unavailable: number; total: number }
    | { state: 'failed' };

/** Planning reads every voxel, so a frame is planned once per image. */
const frameCache = new WeakMap<Image3D, RotatedMipFrame>();

/**
 * The cached rotated-projection frame of an image, or null while slices are
 * arriving: a partial footprint would crop away the rest.
 * @throws if the grid is empty or does not match the voxels.
 */
export function planImageRotatedMipFrame(
    image: Image3D
): RotatedMipFrame | null {
    if (!image.isComplete) {
        return null;
    }
    const cached = frameCache.get(image);
    if (cached) {
        return cached;
    }
    const grid = image.grid;
    const frame = planRotatedMipFrame({
        voxels: image.floatPixelData,
        sizeVoxels: [grid.sizeX, grid.sizeY, grid.sizeZ],
        spacingMm: [grid.resX, grid.resY, grid.resZ],
        orientation: grid.getOrientation(),
    });
    frameCache.set(image, frame);
    return frame;
}

const ANTERIOR_AZIMUTH_DEG = 0;

/** Unchanged azimuth time before the whole volume replaces the footprint. */
export const SETTLE_DELAY_MS = 150;

interface RestProjection {
    frame: RotatedMipFrame;
    azimuthDeg: number;
    mip: RotatedMip | null;
}

/** Null on a throw. The warning carries no identifier (PHI). */
function projectOrNull(
    image: Image3D,
    frame: RotatedMipFrame,
    azimuthDeg: number,
    coverage: RotatedMipCoverage
): RotatedMip | null {
    try {
        return computeRotatedMip(
            { voxels: image.floatPixelData },
            frame,
            azimuthDeg,
            coverage
        );
    } catch (error) {
        console.warn(`useRotatedMip: ${coverage} projection failed`, error);
        return null;
    }
}

/** Null when the projection has no finite value. */
function windowOf(mip: RotatedMip): DisplayWindow | null {
    const finite = mip.rayMaximum.filter(Number.isFinite);
    if (finite.length === 0) {
        return null;
    }
    const [centre, width] = percentileWindow(finite);
    return [centre - width / 2, centre + width / 2];
}

/**
 * The MIP of `image` at `azimuthDeg` with its window, or why there is none.
 * Serves the footprint while turning and the whole volume once settled; the
 * two differ only on background. Undefined with no image.
 */
export function useRotatedMip(
    image: Image3D | undefined,
    azimuthDeg: number
): RotatedMipResult | undefined {
    const progress = useImageStreamProgress(image);
    const isComplete = image !== undefined && progress.isComplete;

    const frame = useMemo((): RotatedMipFrame | null => {
        if (!image || !isComplete) {
            return null;
        }
        try {
            return planImageRotatedMipFrame(image);
        } catch (error) {
            console.warn('useRotatedMip: projection frame failed', error);
            return null;
        }
    }, [image, isComplete]);

    const anterior = useMemo(
        (): RotatedMip | null =>
            image && frame
                ? projectOrNull(image, frame, ANTERIOR_AZIMUTH_DEG, 'volume')
                : null,
        [image, frame]
    );
    const autoWindow = useMemo(
        (): DisplayWindow | null => (anterior ? windowOf(anterior) : null),
        [anterior]
    );

    const [rest, setRest] = useState<RestProjection | null>(null);
    const isAnterior = azimuthDeg === ANTERIOR_AZIMUTH_DEG;
    const isAtRest =
        rest !== null && rest.frame === frame && rest.azimuthDeg === azimuthDeg;
    // A frame with no footprint is already projected whole.
    const needsRest =
        frame !== null && frame.footprint !== null && !isAnterior && !isAtRest;

    const turning = useMemo(
        (): RotatedMip | null =>
            image && frame && !isAnterior && !isAtRest
                ? projectOrNull(image, frame, azimuthDeg, 'footprint')
                : null,
        [image, frame, azimuthDeg, isAnterior, isAtRest]
    );

    useEffect(() => {
        if (!image || !frame || !needsRest) {
            return undefined;
        }
        const timer = setTimeout(() => {
            setRest({
                frame,
                azimuthDeg,
                mip: projectOrNull(image, frame, azimuthDeg, 'volume'),
            });
        }, SETTLE_DELAY_MS);
        return () => clearTimeout(timer);
    }, [image, frame, azimuthDeg, needsRest]);

    if (!image) {
        return undefined;
    }
    if (!progress.isComplete) {
        return progress.isIncompleteFinal
            ? {
                  state: 'imageIncomplete',
                  unavailable: progress.unavailableSliceCount,
                  total: progress.expectedSliceCount,
              }
            : {
                  state: 'waitingForSlices',
                  loaded: progress.loadedSliceCount,
                  total: progress.expectedSliceCount,
              };
    }
    const mip = isAnterior ? anterior : isAtRest ? rest.mip : turning;
    return mip && autoWindow
        ? { state: 'ready', mip, autoWindow }
        : { state: 'failed' };
}
